import { createSelector, createSlice } from "@reduxjs/toolkit";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";
import { RootState } from "../../store";
import { FormatType } from "../../ExtendedProperty/ExtendedProperty";
import { Address, AddressForm, AddressType } from "../model/address";
import { fetchAddressById } from "./asyncThunks";

export const initialAddressFormState = {
    form: {
        open: false,
        title: "",
        address: {
            primaryKeyId: "00000000-0000-0000-0000-000000000000",
            contactId: null,
            organizationId: null,
            addressType: [] as AddressType[],
            name: "",
            lastName: "",
            firstName: "",
            daytimePhoneNumber: "",
            eveningPhoneNumber: "",
            email: "",
            line1: "",
            line2: "",
            city: "",
            state: "",
            countryCode: "",
            countryName: "",
            postalCode: "",
            regionCode: "",
            extendedProperties: [] as Property[],
        } as Address,
        extendedPropertyErrors: [] as Error[],
    },
};

export const addressFormSlice = createSlice({
    name: "addressForm",
    initialState: initialAddressFormState,
    reducers: {
        onOpenDialog: (state, action): void => {
            state.form = {
                ...action.payload.form,
                address: {
                    ...action.payload.form.address,
                    extendedProperties: action.payload.form.address.extendedProperties?.map((property: Property) => {
                        return FormatType[property.metaFieldType] !== FormatType.DateTime
                            ? property
                            : {
                                  ...property,
                                  value: property.value ? new Date(property.value).toISOString().slice(0, 16) : "",
                              };
                    }),
                },
            };
        },
        changeExtendedPropertyErrors: (state, action): void => {
            let { name, message } = action.payload;
            let error = state.form.extendedPropertyErrors.find((error: Error) => error.name === name);
            if (error) {
                state.form.extendedPropertyErrors = state.form.extendedPropertyErrors
                    .filter((error: Error) => {
                        if (error.name === name && message === "") return false;
                        return true;
                    })
                    .map((error: Error) => {
                        if (error.name === name) return { ...error, message: message };
                        return error;
                    });
            } else {
                if (message) {
                    state.form.extendedPropertyErrors.push({ name: name, message: message });
                }
            }
        },
        resetForm: (state): void => {
            state.form = initialAddressFormState.form;
        },
    },
    extraReducers: {
        [`${fetchAddressById.fulfilled}`]: (state, action): void => {
            state.form.address = {
                ...action.payload,
                contactId: action.payload?.contactId ?? state.form?.address?.contactId,
                organizationId: action.payload?.organizationId ?? state.form?.address?.organizationId,
                extendedProperties: action.payload.extendedProperties?.map((property: Property) => {
                    return FormatType[property.metaFieldType] !== FormatType.DateTime
                        ? property
                        : {
                              ...property,
                              value: property.value ? new Date(property.value).toISOString().slice(0, 16) : "",
                          };
                }),
                isFetched: true,
                isNew: state.form.address.isNew,
                idx: state.form.address.idx
            };
        },
    },
});

// Actions
export const { onOpenDialog, changeExtendedPropertyErrors, resetForm } = addressFormSlice.actions;

// Selectors
export const formSelector = createSelector(
    (state: RootState): AddressForm => state.addressForm.form,
    (form: AddressForm): AddressForm => form
);
